import { useEffect, useRef, useState } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { CgTwilio } from "react-icons/cg";
import { FaCode, FaGlobe, FaPlay, FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import { CTABanner } from "../../Components/PortfolioBlocks";

import BrandButton from "../../Components/BrandButton";

const ctaAccent = { base: "#2563EB", hover: "#1D4ED8", dark: "#1E40AF", soft: "#EFF6FF", rgb: "30, 64, 175" };

const projects = [
    {
      title: "BeneSyst Call Solutions",
      category: "Client Portal",
      desc: "A client portal to track calls, scheduled appointments and notifications in one place.",
      img: "/gallery/web-apps/benesyst-client-appointments.webp",
    },
  {
    title: "SOS SMS",
    category: "SMS Platform",
    desc: "Number management and messaging tools built for fast, reliable delivery.",
    img: "/gallery/web-apps/sos-sms.webp",
  },
  {
    title: "SecondBloom",
    category: "Auction Platform",
    desc: "Live auction listings with filters, pricing and seller tools.",
    img: "/gallery/web-apps/secondbloom-auctions.webp", 
  },
  {
    title: "BeneSyst Call Solutions",
    category: "Admin Dashboard",
    desc: "Call and appointment analytics with a clean admin panel.",
    img: "/gallery/web-apps/benesyst-call-solutions.webp", 
  },
  {
    title: "AI Support Automation",
    category: "AI Dashboard",
    desc: "AI-drafted email replies and support workflow automation.",
    img: "/gallery/web-apps/ai-support-automation.webp", 
  },
];

const ProjectCard = ({ p, priority }) => (
  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-lg">
    <div className="flex items-center gap-1.5 border-b border-gray-200 bg-gray-100 px-3 py-2">
      <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-yellow-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-green-300" />
    </div>
    <div className="bg-gray-100" style={{ aspectRatio: "9 / 4" }}>
      <img
        src={p.img}
        alt={`${p.title} - ${p.category}`}
        width="1800"
        height="800"
        loading={priority ? "eager" : "lazy"}
        fetchpriority={priority ? "high" : "auto"}
        decoding="async"
        className="h-full w-full object-cover"
      />
    </div>
    <div className="p-5 sm:p-6">
      <span className="inline-block rounded-full bg-[#98c4ff40] px-3 py-1 text-xs font-semibold text-[#2563EB]">
        {p.category}
      </span>
      <h3 className="mt-3 text-xl font-semibold text-slate-900 sm:text-2xl">{p.title}</h3>
      <p className="mt-1 text-sm text-slate-600 sm:text-base">{p.desc}</p>
    </div>
  </div>
);

export const WebApps = () => {

  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);



  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.6,
        ease: "easeOut",
      },
    }),
  };

  const fadeUp2 = {
    hidden: { opacity: 0, y: 30 },
    visible: (delay = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", delay },
    }),
  };

  const headingRef = useRef(null);
  const paragraphRef = useRef(null);

  const headingControls = useAnimation();
  const paragraphControls = useAnimation();

  const isHeadingInView = useInView(headingRef, { once: true, amount: 0.4 });
  const isParagraphInView = useInView(paragraphRef, {
    once: true,
    amount: 0.4,
  });

  useEffect(() => {
    if (isHeadingInView) {
      headingControls.start("visible");
    }
  }, [isHeadingInView]);

  useEffect(() => {
    if (isParagraphInView) {
      paragraphControls.start("visible");
    }
  }, [isParagraphInView]);

  const imageFrames = projects;

  const [index, setIndex] = useState(0);
  const platformRef = useRef(null);
  const width = 1050;

  const next = () => {
    if (index < imageFrames.length - 1) setIndex(index + 1);
  };

  const prev = () => {
    if (index > 0) setIndex(index - 1);
  };

  const goTo = (target) => {
    if (target === "end") setIndex(imageFrames.length - 1);
    else if (!isNaN(target)) setIndex(Number(target));
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [index]);

  useEffect(() => {
    if (platformRef.current) {
      platformRef.current.style.right = `${index * width}px`;
    }
  }, [index]);


  const [callState, setCallState] = useState("incoming");


  return (
    <section className="text-black">
      <div className="mt-20 font-sans overflow-x-hidden relative ">
        <section className="relative z-10 py-16 md:py-24 px-6 md:px-12 lg:px-24">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center  text-center lg:text-left">
            <div className="space-y-8">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                custom={0}
              >
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#98c4ff81]  text-primary-dark font-medium">
                  <span className="relative flex h-3 w-3 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4A9AFA] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-primary-dark"></span>
                  </span>
                  <span className="text-[#4A9AFA]">Web Apps</span>
                </div>
              </motion.div>
              <motion.h1
                className="text-4xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight text-gray-900  text-center lg:text-left"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
               Powerful Web App Solutions{" "}
                <span className="text-[#4A9AFA]">
                  for Modern Businesses
                </span>
              </motion.h1>
              <motion.p
                className="text-lg md:text-xl text-gray-600 max-w-2xl mt-2 text-center lg:text-left md:text-center"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
              >
                We craft high-performance web applications tailored to your
                unique business needs - from dashboards and CRMs to e-commerce
                platforms and admin portals.
                <span className="ml-2 font-semibold">
                  Streamline operations, improve user experiences, and scale
                  efficiently with our full-stack web solutions.
                </span>
              </motion.p>

              <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="flex flex-wrap gap-4 justify-center lg:justify-start"
            >
             <BrandButton as={Link} to="/contact" accent={ctaAccent}>
                  Get a Free Quote
                </BrandButton>
            </motion.div>
             <motion.p
            className="text-sm md:text-md text-gray-600 max-w-2xl "
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
          >
            See the web apps we've built for our clients.
          </motion.p>
            </div>

            <div className="relative">
              <motion.div
                initial={{ y: 30 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="relative w-full max-w-md mx-auto rounded-3xl shadow-2xl bg-[#4a9afa17] border border-gray-100 p-6"
              >
                <div className="bg-white/80 backdrop-blur-sm border border-gray-100 rounded-2xl  text-center relative">
                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    custom={4}
                    className="absolute -top-10 p-8 text-5xl -right-20 text-[#4A9AFA] rounded-2xl bg-[#043b9b13] backdrop-blur-sm shadow-lg z-20"
                  >
                    <FaGlobe />
                  </motion.div>

                  <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    custom={6}
                    className="absolute top-64 p-4 text-5xl -left-14 text-[#4A9AFA] rounded-2xl bg-[#043b9b13] backdrop-blur-sm shadow-lg z-20"
                  >
                    <FaCode />
                  </motion.div>

                  <div className="">
                    <div className="w-full  rounded-xl overflow-hidden shadow-inner ">
                      <img
                        src="/6342887.webp"
                        alt="Web app dashboard UI components"
                        width="800"
                        height="800"
                        loading="eager"
                        fetchpriority="high"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-10 pb-16 text-center lg:pt-20">
        <motion.p
          className="mx-auto -mt-4 max-w-2xl text-md tracking-widest text-slate-700 sm:mt-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          Our
          <span className="border-b border-dotted border-slate-300 ml-2">
            Work
          </span>
        </motion.p>

        <motion.h2
          className="mx-auto mt-4 max-w-4xl font-display text-3xl sm:text-3xl md:text-5xl font-semibold tracking-tight text-slate-900  leading-tight"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.3 }}
          viewport={{ once: true }}
        >
          <span className="inline-block">Web Apps We Build </span>
        </motion.h2>

        <motion.p
          className="mx-auto mt-9 max-w-2xl text-lg tracking-tight text-slate-600 sm:mt-6"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.6 }}
          viewport={{ once: true }}
        >
          Explore a showcase of powerful, production-ready web applications
          we've built - each crafted for speed, scalability, and an exceptional
          user experience.{" "}
        </motion.p>

        {/* Desktop slider */}
        <motion.div
          className="gallery w-full rounded mx-auto mt-16 hidden md:block"
          style={{ maxWidth: `${width}px` }}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.9 }}
          viewport={{ once: true }}
        >
          <div className="content-area w-full overflow-hidden">
            <div
              className="platform flex transition-transform duration-300"
              style={{
                width: `${imageFrames.length * 100}%`,
                transform: `translateX(-${(index * 100) / imageFrames.length}%)`,
              }}
            >
             {imageFrames.map((p) => (
                <div
                  key={p.img}
                  className="each-frame flex-none px-1"
                  style={{ width: `${100 / imageFrames.length}%` }}
                >
                  <ProjectCard p={p} />
                </div>
              ))}
            </div>
          </div>

        <div className="flex items-center justify-center gap-3 p-4">
            <motion.svg
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 1.1 }}
              onClick={prev}
              className={`w-10 cursor-pointer rounded-lg bg-[#4A9AFA] p-2 ${index === 0 ? "opacity-40" : ""}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#fff"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </motion.svg>
            <motion.svg
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 1.1 }}
              onClick={next}
              className={`w-10 cursor-pointer rounded-lg bg-[#4A9AFA] p-2 ${index === imageFrames.length - 1 ? "opacity-40" : ""}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#fff"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </motion.svg>
          </div>
        </motion.div>

      {/* Mobile stack */}
        <div className="mt-10 flex flex-col gap-6 md:hidden">
          {projects.map((p) => (
            <ProjectCard key={p.img} p={p} />
          ))}
        </div>
      </div>

      <CTABanner
        title="Need a web app built for your business?"
        text="Tell us what you need, from dashboards and CRMs to e-commerce and admin portals, and we'll send you a free quote."
        buttonLabel="Get a Free Quote"
        color="#2563EB"
        hoverBg="#eff6ff"
      />
    </section>
  )
}
